import axios from 'src/api/axiosInstances/privateAxiosInstancePrice';
import { useEffect, useState } from 'react';
import auth from 'src/services/auth';
import notification from 'antd/lib/notification';

type ShallowCleanupListRequest = {
  verticalName?: string;
  siteIds?: string;
  optionSelect?:string;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  size?: number;
  page?: number;
  sort?: string;
};

const transformParams = (params: ShallowCleanupListRequest | null) => {
  const { optionSelect, dateRange, ...newParams }: any = params;

  return {
    ...newParams,
    ...(optionSelect !== undefined && { optionSelect }),
    startDate: dateRange?.startDate || null,
    endDate: dateRange?.endDate || null,
  };
};

const getAsyncData = async (url: string, userId: string): Promise<any> => {
  try {
    const response: any = await axios.get(url, {
      headers: { userId },
    });

    if (response.status !== 200) {
      if (response.status === 206) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return getAsyncData(url, userId);
      } else {
        throw new Error(`Response status is ${response.status}. Stopping retries.`);
      }
    }
    return response.data;
  } catch (error) {
    console.error(`Error fetching async data:`, error);
    throw error;
  }
};

export const useGetShallowCleanupList = (params: ShallowCleanupListRequest | null) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [productList, setProductList] = useState<any[]>([]);
  const userId = auth.getUserId();

  useEffect(() => {
    const fetchData = async () => {
      if (params && params.verticalName && (params?.dateRange || params?.optionSelect)) {
        setLoading(true);
        try {
          const response = await axios.get('/nucleus/shallowcleanup/cleanup', {
            headers: { userId },
            params: transformParams(params),
          });

          if (response && response.status === 202 && response.headers?.location) {
            const location = response.headers.location;

            try {
              await new Promise((resolve) => setTimeout(resolve, 1000));
              const asyncData = await getAsyncData(`/nucleus/asynch?fileName=${location}`, userId);
              
              if (asyncData) {
                setProductList(asyncData.ownerCleanups);
                setTotalRecords(asyncData.totalCount);
              } else {
                setProductList([]);
                setTotalRecords(0);
              }
            } catch (error) {
              console.error('Error fetching async data:', error);
              setProductList([]);
              setTotalRecords(0);
            }
          } else {
            console.error('Location header missing in response:', response);
            setProductList([]);
            setTotalRecords(0);
          }
        } catch (error) {
          console.error('Error fetching initial data:', error);
          setProductList([]);
          setTotalRecords(0);
        } finally {
          setLoading(false);
        }
      } else {
        setProductList([]);
        setTotalRecords(0);
      }
    };

    fetchData();
  }, [params, userId]);

  return [loading, totalRecords, productList];
};


export const useDeleteProductFromShallow = (
  params: ShallowCleanupListRequest | null,
  productList: any[],
  setDeletedProductList: any,
  onUpdate?: any,
  setRequestLoading?: (loading: boolean) => void
) => {
  useEffect(() => {
    const retryFetchAsyncData = async (location: string, userId: string, attempt: number) => {
      const delay = 1000; // Delay between retries

      try {
        const response: any = await axios.get(`/nucleus/asynch?fileName=${location}`, {
          headers: { userId },
        });

        if (response.status !== 200) {
          if (response.status === 206) {
            setTimeout(() => retryFetchAsyncData(location, userId, attempt + 1), delay);
          } else {
            setRequestLoading && setRequestLoading(false);
            throw new Error(`Response status is ${response.status}. Stopping retries.`);
          }
        }

        const asyncData = response.data;
        if (response.status === 200 && asyncData.successCode === 200) {
          setRequestLoading && setRequestLoading(false);
          setDeletedProductList({
            ...params,
            pagestart: 0,
          });
          onUpdate && onUpdate(productList.length);
          notification.success({
            message: asyncData.successMessage,
            placement: 'topRight',
          });
        } else {
          throw new Error('Async data is not successful.');
        }
      } catch (error) {
        console.error(`Attempt ${attempt} failed. Retrying...`, error);
        setRequestLoading && setRequestLoading(false);
      }
    };

    if (productList.length > 0 && params && params.verticalName && (params?.dateRange || params?.optionSelect)) {
      const userId = auth.getUserId();
      setRequestLoading && setRequestLoading(true);
      axios
        .delete('/nucleus/shallowcleanup/cleanup', {
          params: {
            verticalName: params.verticalName,
            startDate: params?.dateRange?.startDate,
            endDate: params?.dateRange?.endDate,
            optionSelect: params.optionSelect,
          },
          data: {
            ids: productList,
          },
        })
        .then((response: any) => {
          if (response && response.headers && response.headers.location) {
            retryFetchAsyncData(response.headers.location, userId, 1); // Continue retrying indefinitely
          } else {
            setRequestLoading && setRequestLoading(false);
            console.error('Location header missing in response:', response);
          }
        })
        .catch((error: any) => {
          setRequestLoading && setRequestLoading(false);
          console.error('Error making delete request:', error);
        });
    }
  }, [params, productList, setDeletedProductList, onUpdate, setRequestLoading]);

  return [];
};


export const useExportShallowCleanup = (
  params: ShallowCleanupListRequest | null,
  isExport: boolean,
  setExport: (value: boolean) => void
): [boolean, File | null] => {
  const [exportLoading, setLoading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const userId = auth.getUserId();

  useEffect(() => {
    const fetchData = async () => {
      if (isExport && params && params.verticalName && (params?.dateRange || params?.optionSelect)) {
        setLoading(true);
        setFile(null);
        try {
          const response: any = await axios.get('/nucleus/shallowcleanup/export', {
            headers: { userId },
            params: {
              siteIds: params.siteIds,
              verticalName: params.verticalName,
              startDate: params?.dateRange?.startDate,
              endDate: params?.dateRange?.endDate,
              optionSelect: params.optionSelect,
            },
          });

          if (response && response.status === 202 && response.headers && response.headers.location) {
            setLoading(false);
            setExport(false);
            const location = response.headers.location;
            try {
              const asyncData = await getAsyncDataExport(`/nucleus/asynch?fileName=${location}`, userId, 0);
              if (asyncData && asyncData.ownerCleanups) {
                const csvFile = convertAndDownloadCSV(asyncData.ownerCleanups, location);
                setFile(csvFile);
              }
              setLoading(false);
            } catch (error) {
              setExport(false);
              setLoading(false);
              console.error('Error fetching async data:', error);
            }
          }
        } catch (error) {
          setExport(false);
          setLoading(false);
          console.error('Error fetching export data:', error);
        }
      }
    };

    fetchData();
  }, [isExport, params, setExport]);

  return [exportLoading, file];
};

const getAsyncDataExport = async (url: string, userId: string, step: number): Promise<any> => {
  try {
    const response: any = await axios.get(url, {
      headers: { userId },
    });

    if (response.status !== 200) {
      if (response.status === 206) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return getAsyncDataExport(url, userId, step + 1);
      } else {
        throw new Error(`Response status is ${response.status}. Stopping retries.`);
      }
    }

    return response.data;
  } catch (error) {
    console.error(`Error fetching async data:`, error);
    throw error;
  }
};

const convertToCsv = (json: any[]): string => {
  if (json.length === 0) return '';
  const header = Object.keys(json[0]);
  const csvRows = [
    header.join(','),
    ...json.map((row) => header.map((key) => (row[key] !== undefined ? row[key] : '')).join(',')),
  ];
  return csvRows.join('\n');
};

const convertAndDownloadCSV = (data: any[], fileName: string): File => {
  const csvData = convertToCsv(data);
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const file = new File([blob], fileName, { type: 'text/csv' });

  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  a.click();
  return file;
};
